import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { DateInput } from "@/components/ui/date-input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { useSection8, HQSChecklistItem } from "@/hooks/use-section8";
import {
  Plus,
  ClipboardList,
  CheckCircle2,
  XCircle,
  AlertTriangle,
} from "lucide-react";
import { DomainTermInfo } from "@/components/ui/domain-term-info";

export function HQSInspectionTab() {
  const {
    cases,
    inspections,
    createInspection,
    updateInspection,
    getChecklistForInspection,
    updateChecklistItem,
  } = useSection8();
  const [open, setOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState<string | null>(
    null,
  );
  const [checklist, setChecklist] = useState<HQSChecklistItem[]>([]);
  const [loadingChecklist, setLoadingChecklist] = useState(false);

  const loadChecklist = async (inspectionId: string) => {
    setLoadingChecklist(true);
    try {
      const items = await getChecklistForInspection(inspectionId);
      setChecklist(items);
      setSelectedInspection(inspectionId);
    } finally {
      setLoadingChecklist(false);
    }
  };

  const handleCreateInspection = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    createInspection.mutate(
      {
        section8_case_id: fd.get("section8_case_id") as string,
        inspection_date: (fd.get("inspection_date") as string) || undefined,
        inspector_name: (fd.get("inspector_name") as string) || undefined,
      },
      { onSuccess: () => setOpen(false) },
    );
  };

  const toggleItem = (item: HQSChecklistItem) => {
    const newPassed =
      item.passed === true ? false : item.passed === false ? null : true;
    updateChecklistItem.mutate({ id: item.id, passed: newPassed });
    setChecklist((prev) =>
      prev.map((i) => (i.id === item.id ? { ...i, passed: newPassed } : i)),
    );
  };

  // Group checklist by room
  const rooms = checklist.reduce<Record<string, HQSChecklistItem[]>>(
    (acc, item) => {
      if (!acc[item.room]) acc[item.room] = [];
      acc[item.room].push(item);
      return acc;
    },
    {},
  );

  const totalItems = checklist.length;
  const passedItems = checklist.filter((i) => i.passed === true).length;
  const failedItems = checklist.filter((i) => i.passed === false).length;
  const criticalFailed = checklist.filter(
    (i) => i.passed === false && i.is_critical,
  ).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-foreground flex items-center gap-1.5">
            <span>HQS Inspections</span>
            <DomainTermInfo termId="HQS" size="md" />
          </h3>
          <p className="text-sm text-muted-foreground">
            Room-by-room Housing Quality Standards checklist
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button size="sm">
              <Plus className="h-4 w-4 mr-1" /> Schedule Inspection
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Schedule HQS Inspection</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateInspection} className="space-y-3">
              <div>
                <Label>Section 8 Case</Label>
                <Select name="section8_case_id" required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select case" />
                  </SelectTrigger>
                  <SelectContent>
                    {cases.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        Case #{c.id.slice(0, 8)} — {c.pha_name || "No PHA"}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Inspection Date</Label>
                <DateInput name="inspection_date" />
              </div>
              <div>
                <Label>Inspector Name</Label>
                <Input name="inspector_name" />
              </div>
              <Button
                type="submit"
                className="w-full"
                disabled={createInspection.isPending}
              >
                Create Inspection & Checklist
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Inspection list */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {inspections.map((insp) => (
          <Card
            key={insp.id}
            className={`shadow-soft cursor-pointer transition-colors hover:border-primary/50 ${selectedInspection === insp.id ? "border-primary" : ""}`}
            onClick={() => loadChecklist(insp.id)}
          >
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <Badge
                  variant="outline"
                  className={
                    insp.status === "passed"
                      ? "bg-emerald-500/10 text-emerald-500"
                      : insp.status === "failed"
                        ? "bg-destructive/10 text-destructive"
                        : "bg-amber-500/10 text-amber-500"
                  }
                >
                  {insp.status}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {insp.inspection_date || "TBD"}
                </span>
              </div>
              <p className="text-sm font-medium">
                {insp.inspector_name || "Inspector TBD"}
              </p>
              <p className="text-xs text-muted-foreground">
                Case #{insp.section8_case_id.slice(0, 8)}
              </p>
            </CardContent>
          </Card>
        ))}
        {inspections.length === 0 && (
          <Card className="shadow-soft col-span-full">
            <CardContent className="py-8 text-center">
              <ClipboardList className="h-10 w-10 mx-auto text-muted-foreground/50 mb-2" />
              <p className="text-sm text-muted-foreground">
                No inspections scheduled. Create a case first, then schedule an
                inspection.
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Checklist view */}
      {selectedInspection && (
        <div className="space-y-4">
          {/* Stats bar */}
          <div className="grid grid-cols-4 gap-3">
            <Card className="shadow-soft">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-foreground">
                  {totalItems}
                </p>
                <p className="text-xs text-muted-foreground">Total Items</p>
              </CardContent>
            </Card>
            <Card className="shadow-soft">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-emerald-500">
                  {passedItems}
                </p>
                <p className="text-xs text-muted-foreground">Passed</p>
              </CardContent>
            </Card>
            <Card className="shadow-soft">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-destructive">
                  {failedItems}
                </p>
                <p className="text-xs text-muted-foreground">Failed</p>
              </CardContent>
            </Card>
            <Card className="shadow-soft">
              <CardContent className="p-3 text-center">
                <p className="text-2xl font-bold text-amber-500">
                  {criticalFailed}
                </p>
                <p className="text-xs text-muted-foreground">Critical Fails</p>
              </CardContent>
            </Card>
          </div>

          {/* Room-by-room checklist */}
          {Object.entries(rooms).map(([room, items]) => {
            const roomPassed = items.filter((i) => i.passed === true).length;
            const roomTotal = items.length;
            return (
              <Card key={room} className="shadow-soft">
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-sm font-medium">
                      {room}
                    </CardTitle>
                    <span className="text-xs text-muted-foreground">
                      {roomPassed}/{roomTotal} passed
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="space-y-1.5">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-3 p-2 rounded-md hover:bg-muted/50 cursor-pointer"
                      onClick={() => toggleItem(item)}
                    >
                      <div className="flex-shrink-0">
                        {item.passed === true ? (
                          <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                        ) : item.passed === false ? (
                          <XCircle className="h-5 w-5 text-destructive" />
                        ) : (
                          <div className="h-5 w-5 rounded-full border-2 border-muted-foreground/30" />
                        )}
                      </div>
                      <span
                        className={`text-sm flex-1 ${item.passed === false ? "text-destructive" : "text-foreground"}`}
                      >
                        {item.item_name}
                      </span>
                      {item.is_critical && (
                        <Badge
                          variant="outline"
                          className="text-[10px] bg-amber-500/10 text-amber-500 border-amber-500/20"
                        >
                          <AlertTriangle className="h-3 w-3 mr-0.5" />
                          Critical
                        </Badge>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}

          {/* Update inspection status */}
          <div className="flex gap-2">
            <Button
              variant="outline"
              className="text-emerald-500"
              onClick={() => {
                const insp = inspections.find(
                  (i) => i.id === selectedInspection,
                );
                if (insp)
                  updateInspection.mutate({ id: insp.id, status: "passed" });
              }}
            >
              <CheckCircle2 className="h-4 w-4 mr-1" /> Mark Passed
            </Button>
            <Button
              variant="outline"
              className="text-destructive"
              onClick={() => {
                const insp = inspections.find(
                  (i) => i.id === selectedInspection,
                );
                if (insp)
                  updateInspection.mutate({ id: insp.id, status: "failed" });
              }}
            >
              <XCircle className="h-4 w-4 mr-1" /> Mark Failed
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
